import { SearchPageChrome } from "@/components/search/page-chrome"
import { useShowEducatorProps } from "./page-props"
import { SheetPage, SheetPageTrigger } from "@/components/ui/sheet-page"
import { ActionButton } from "@/components/shared/action-buttons"
import { useTranslation } from "react-i18next"
import { Users } from "lucide-react"
import { PageBody } from "@/components/ui/layout"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ParseKeys } from "i18next"
import { CycleCalendar } from "@/components/shared/cycle-calendar"
import { useGetColours } from "@/lib/useGetColours"
import { useFullCalendarApi } from "@/lib/fullCalendar"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import {
  calculateEventStartCalendarTimezoneCoerced,
  displayPlainTime,
  getTimezoneAbbreviation,
} from "@/lib/timetableZonedDatetime"

export default function Page() {
  const { relief_listing } = useShowEducatorProps()

  return (
    <>
      <SearchPageChrome
        identity={{ name: relief_listing.title }}
        action={<Actions />}
      />
      <PageBody>
        <DetailsCard />
        <TimetableCard />
      </PageBody>
    </>
  )
}

function Actions() {
  const { listing_uuid, relief_listing, capabilities } = useShowEducatorProps()
  const { t } = useTranslation("search", {
    keyPrefix: "show.educator.relief_listing",
  })

  if (!capabilities.can_apply || !relief_listing.applyable) {
    return null
  }

  return (
    <SheetPage
      href={`/educators/relief/applications/new`}
      data={{ educators_relief_listing_id: listing_uuid }}
    >
      <SheetPageTrigger asChild>
        <ActionButton Icon={Users} variant="solid">
          {t("apply")}
        </ActionButton>
      </SheetPageTrigger>
    </SheetPage>
  )
}

function DetailsCard() {
  const { t } = useTranslation("search", {
    keyPrefix: "show.educator.relief_listing",
  })
  const { t: tTags } = useTranslation("listing_profile_tags")
  const { relief_listing } = useShowEducatorProps()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-4xl">{relief_listing.title}</CardTitle>
        <div className="flex flex-row flex-wrap gap-4">
          <Badge>
            {t("duration", {
              start: relief_listing.start_date,
              end: relief_listing.end_date,
            })}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <span className="text-muted-foreground text-sm">
          {t("description")}
        </span>
        <p>{relief_listing.description}</p>
      </CardContent>
      <CardFooter className="flex flex-col items-start gap-2">
        <span className="text-muted-foreground text-sm">{t("tags")}</span>
        <div className="flex flex-row flex-wrap gap-4">
          {relief_listing.tags.map((tag, index) => (
            <Badge
              className="bg-brand-light-grey border-brand-dark-grey/40"
              key={index}
            >
              {tTags(tag as ParseKeys<"listing_profile_tags">)}
            </Badge>
          ))}
        </div>
      </CardFooter>
    </Card>
  )
}

const calendarTimezoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
const calendarStartInstant = Temporal.Instant.from("2025-01-06T00:00:00Z")

function TimetableCard() {
  const { t } = useTranslation("search", {
    keyPrefix: "show.educator.relief_listing",
  })
  const { relief_listing } = useShowEducatorProps()
  const { title, cycle_length, timezone_id: eventTimezoneId } = relief_listing

  const [calendarRef] = useFullCalendarApi("LearnerReliefListing")
  const getColours = useGetColours()

  const events = relief_listing.sessions.map(
    ({ day, duration, start_time }, index) => {
      const id = crypto.randomUUID()
      return {
        cycleLength: cycle_length,
        startTime: start_time,
        duration,
        day,
        id,
        eventTimezoneId,
        eventTitle: title,
        extendedProps: {
          index,
          colours: getColours(id, index, relief_listing.sessions.length),
        },
      }
    },
  )

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t("timetable")}</CardTitle>
        <dl className="[&_dt]:text-muted-foreground flex flex-row gap-8 [&_dt]:text-sm">
          <div>
            <dt>{t("start_date")}</dt>
            <dd>{relief_listing.start_date}</dd>
          </div>

          <div>
            <dt>{t("end_date")}</dt>
            <dd>{relief_listing.end_date}</dd>
          </div>
        </dl>
      </CardHeader>
      <CardContent>
        <CycleCalendar
          calendarRef={calendarRef}
          events={events}
          cycleLength={cycle_length}
          interactive={false}
          calendarTimezoneId={calendarTimezoneId}
          calendarStartInstant={calendarStartInstant}
          aspectRatio={1.5}
          height={undefined}
          eventContent={({
            event: {
              title,
              extendedProps: { index },
            },
          }) => {
            const session = relief_listing.sessions[index]
            if (!session) return null
            return <Event index={index} title={title} />
          }}
        />
      </CardContent>
    </Card>
  )
}

function Event({ index, title }: { index: number; title: string }) {
  const { relief_listing } = useShowEducatorProps()
  const { t } = useTranslation("search", {
    keyPrefix: "show.educator.relief_listing",
  })
  const { timezone_id: eventTimezoneId } = relief_listing
  const session = relief_listing.sessions[index]
  if (!session) return null

  const { start_time: startTime, duration, day } = session

  const calendarTimezoneIsNotEventTimezone =
    calendarTimezoneId !== eventTimezoneId
  const startTimeCoerced = calculateEventStartCalendarTimezoneCoerced({
    startTime,
    day,
    eventTimezoneId,
    calendarTimezoneId,
    startTemporalZonedDateTime: calendarStartInstant.toZonedDateTimeISO("UTC"),
  })
  const endTimeCoerced = startTimeCoerced.add({ minutes: duration })

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          className={cn(
            "fc-event-main-frame w-full flex-col! items-start! overflow-hidden text-start text-xs text-current",
          )}
        >
          <span className="sm:text-sm">{title}</span>
          <span className="">
            {t("times", {
              start: displayPlainTime(startTimeCoerced.toPlainTime()),
              end: displayPlainTime(endTimeCoerced.toPlainTime()),
            })}
          </span>
        </TooltipTrigger>
        <TooltipContent className="text-white">
          <span>{title}</span>
          <br />
          <span>
            {t("times", {
              start: displayPlainTime(startTimeCoerced.toPlainTime()),
              end: displayPlainTime(endTimeCoerced.toPlainTime()),
            })}
            <br />
            {calendarTimezoneIsNotEventTimezone && (
              <span>
                {t("times_timezone", {
                  start: displayPlainTime(Temporal.PlainTime.from(startTime)),
                  end: displayPlainTime(endTimeCoerced.toPlainTime()),
                  timezone: getTimezoneAbbreviation(eventTimezoneId),
                })}
              </span>
            )}
          </span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
